import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OverlayPanel } from 'primeng/overlaypanel';
import moment from 'moment';
import { IPageResult } from '../../../../interfaces/page-result';
import { IIncidentFilter } from '../../../../interfaces/incidents-page-filter';
import {
  BBWTMessageService,
  EventService,
  IncidentsService,
  JobsService,
  UserService,
} from '../../../../services';
import {
  VMEJobScreenIncidentsJob,
  VMEJobScreenIncidentsIncident,
} from '../../../../interfaces/screen-specific/job-screen';
import { IIncidentCloseDTO } from '../../../../interfaces/incident';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { startOfDay } from 'date-fns';

@Component({
  selector: 'vme-job-screen-incidents',
  templateUrl: './vme-job-screen-incidents.component.html',
  styleUrls: ['./vme-job-screen-incidents.component.scss'],
})
export class VMEJobScreenIncidentsComponent implements OnInit, OnDestroy, OnChanges {
  job: VMEJobScreenIncidentsJob;

  @Input() set selectedTask(value: any) {
    this._selectedTask = value;
  }

  private _selectedTask: any;

  @Input() set jobId(value: number) {
    this._jobId = value;
    this.getJob(value);
    this.clearFilter();
  }

  private _jobId: number;

  @Output() onShowHistory: EventEmitter<any> = new EventEmitter<any>();

  loading: boolean;
  page: IPageResult<VMEJobScreenIncidentsIncident> = { items: [], total: 0 };
  filter: IIncidentFilter;
  private loadSubject: Subject<IIncidentFilter>;

  raiseIncidentsForm: UntypedFormGroup;
  typeOpts: SelectItem[] = [];

  closeIncidentDialog: boolean = false;
  closeIncidentForm: UntypedFormGroup;

  // incident item selected for closure
  private closeItem: VMEJobScreenIncidentsIncident;
  closureReasons: SelectItem[] = [];

  public currentUserId: string;

  refreshIncidentsSub: Subscription;

  currentDate = startOfDay(new Date());

  constructor(
    private incidentsService: IncidentsService,
    private formBuilder: UntypedFormBuilder,
    private messageService: BBWTMessageService,
    private jobService: JobsService,
    private userService: UserService,
    private eventService: EventService,
  ) {
    this.loadSubject = new Subject<IIncidentFilter>();

    this.loadSubject
      .asObservable()
      .pipe(debounceTime(500))
      .subscribe((f) => {
        this.loading = true;
        this.incidentsService.getPage(f).then((res) => {
          this.page = res as IPageResult<VMEJobScreenIncidentsIncident>;
          this.loading = false;
        });
      });

    this.raiseIncidentsForm = formBuilder.group({
      typeId: ['', Validators.required],
      comment: [''],
      dueBy: [''],
    });

    this.filter = this._getDefaultFilter();

    this.closeIncidentForm = formBuilder.group({
      closureReasonId: ['', Validators.required],
      closureNotes: ['', [Validators.maxLength(1024)]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[this._jobId]) {
      this.getJob(this._jobId);
    }
  }

  async ngOnInit() {
    this.refreshIncidentsSub = this.eventService.RefreshIncidentsEvent.subscribe(() => {
      this.search();
    });

    this.incidentsService.getCustomTypes().then((res) => {
      this.typeOpts = res;
      this.search();
    });

    this.incidentsService.getClosureReasonsForDropdown().then((res) => {
      this.closureReasons = res;
    });

    this.currentUserId = this.userService.currentUser.id;
  }

  ngOnDestroy() {
    if (this.refreshIncidentsSub) {
      this.refreshIncidentsSub.unsubscribe();
    }
  }

  onGridLazyLoad(event: any) {
    this.filter.first = event.first;
    this.filter.rows = event.rows;
    this.filter.sortField = event.sortField;
    this.filter.sortOrder = event.sortOrder;
    this.search();
  }

  getDateDescription(date: Date, hideTime: boolean = false): string {
    return date ? moment(date).format(hideTime ? "DD MMM 'YY" : "DD MMM 'YY HH:mm") : '';
    //return date ? moment.duration(moment(date).diff(moment())).humanize(true) : 'N/A';
  }

  closeIncident(item: VMEJobScreenIncidentsIncident) {
    // show confirmation/closure reason dialog
    this.closeItem = item;
    this.closeIncidentDialog = true;
  }

  confirmCloseIncident() {
    let closeDto: IIncidentCloseDTO = this.closeIncidentForm.value;
    closeDto.id = this.closeItem.id;
    closeDto.typeId = this.closeItem.typeId;
    closeDto.typeName = this.closeItem.typeName;
    closeDto.jobId = this.closeItem.jobId;
    closeDto.taskId = this.closeItem.taskId;
    closeDto.jobNumber = this.closeItem.jobNumber;
    closeDto.vehicleReg = this.closeItem.vehicleReg;

    //console.log("close DTO: ", closeDto);

    this.incidentsService.close(closeDto).then((res) => {
      this.closeItem.closed = res;
      this.clearClosureVars();
      this.search();
    });
  }

  clearClosureVars() {
    this.closeItem = null;
    this.closeIncidentForm.reset();
    this.closeIncidentDialog = false;
  }

  search() {
    if (!this.filter) {
      this.filter = this._getDefaultFilter();
    }
    this.loadSubject.next(this.filter);
  }

  clearFilter() {
    this.filter = this._getDefaultFilter();
    this.search();
  }

  @ViewChild('assigneeSelectionPanel') assigneeSelectionPanel: OverlayPanel;
  private _currentIncidentForAssignment: VMEJobScreenIncidentsIncident;

  openAssigneeSelectionPanel(event: any, item: VMEJobScreenIncidentsIncident) {
    this._currentIncidentForAssignment = item;
    this.assigneeSelectionPanel.toggle(event);
  }

  confirmAssigneeSelection() {
    this.assignToMe(this._currentIncidentForAssignment);
    this.assigneeSelectionPanel.hide();
  }

  onAssigneeSelectionPanelHide() {
    this._currentIncidentForAssignment = null;
  }

  assignToMe(item: VMEJobScreenIncidentsIncident) {
    this.incidentsService.assigneeToMe(item.id).then(() => this.search());
  }

  raiseIncident() {
    // if selected task is null, set to first task in first order
    let taskId = this.job.orders[0].tasks[0].id;

    if (this._selectedTask) {
      taskId = this._selectedTask.id;
    }

    const data = { jobId: this.job.id, taskId: taskId, ...this.raiseIncidentsForm.value };

    this.incidentsService.raiseJobIncident(data).then(() => {
      this.search();
      this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    });
    this.raiseIncidentsForm.reset();
  }

  private _getDefaultFilter(): IIncidentFilter {
    return {
      first: 0,
      rows: 20,
      sortField: '',
      sortOrder: 1,
      filters: {
        jobId: this._jobId,
        component: 'vme-job-screen-incidents',
      },
    };
  }

  private getJob(jobId: number) {
    this.jobService.getForComponent(jobId, 'vme-job-screen-incidents').then((job) => {
      this.job = job as VMEJobScreenIncidentsJob;
    });
  }
}
